import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * BoostBanner
 *
 * A dismissible nudge that appears on the Dashboard when the user is
 * close to (≥80%) or over their monthly AI query cap. Points to the
 * boost-pack section on /account. Hidden:
 *  - On Free tier (boosts not available there)
 *  - When the user is well within their cap (< 80% used)
 *  - When the user has dismissed it this session
 */
export default function BoostBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = React.useState(false);

  if (!user) return null;
  if (user.tier === "free") return null;
  if (hidden) return null;

  const used = user.ai_queries_used || 0;
  const limit = user.ai_queries_limit || 1;
  const pct = used / Math.max(1, limit);
  if (pct < 0.8) return null;
  const isOver = used >= limit;

  return (
    <div
      className={`relative mb-5 border-l-4 ${isOver ? "border-l-[#B35D4C] bg-[#FBEEEA]" : "border-l-[#C9A961] bg-[#FAF6E8]"} p-4 pr-10`}
      data-testid="boost-banner"
    >
      <button
        onClick={() => setHidden(true)}
        className="absolute top-2 right-2 text-[#6B7280] hover:text-[#1A1A1A] p-1"
        aria-label="Dismiss"
        data-testid="boost-banner-dismiss"
      ><X size={14} strokeWidth={1.5} /></button>
      <div className="flex items-start gap-2.5">
        <Sparkles size={16} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${isOver ? "text-[#B35D4C]" : "text-[#8A6D3B]"}`} />
        <div>
          <div className="font-serif text-[15px] text-[#0D2B52]">
            {isOver
              ? "You've used all your AI queries this month."
              : `You're at ${Math.round(pct * 100)}% of this month's AI queries.`}
          </div>
          <div className="text-[12.5px] text-[#1A1A1A] mt-0.5">
            Top up with a Boost Pack, $5 for 25 extra queries, valid 90 days.{" "}
            <Link to="/account?boost=1" className="text-[#1B6C62] underline hover:no-underline font-medium" data-testid="boost-banner-cta">
              Get a Boost →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
